/**
 * Game event payloads. Extended as systems land; artifacts subscribe to these for
 * proc effects, UI subscribes for toasts/feedback.
 */
export interface GameEvents {
  enemyKilled: { x: number; y: number; gemValue: number; isElite: boolean };
  playerHit: { damage: number; hpAfter: number };
  playerHealed: { amount: number };
  gemCollected: { value: number };
  artifactAcquired: { artifactId: string; proc: 'none' | 'cursed' | 'blessed'; curseId: string | null; quality: number };
  artifactPickedUp: { artifactId: string; quality: number; proc: 'none' | 'cursed' | 'blessed'; curseId: string | null };
  /** Better-quality copy of an equipped artifact replaced it in place. */
  artifactImproved: { artifactId: string; from: number; to: number; proc: 'none' | 'cursed' | 'blessed'; curseId: string | null };
  /** Equal/worse copy of an equipped artifact crumbled on pickup. */
  artifactDust: { artifactId: string; quality: number };
  /** Pickup at capacity — the player must choose a swap (or walk away). */
  artifactSwapPrompt: { artifactId: string; quality: number; proc: 'none' | 'cursed' | 'blessed'; curseId: string | null };
  levelUpQueued: { pendingCount: number };
  circleAppeared: { x: number; y: number };
  bossStarted: { name: string };
  bossPhaseChanged: { phase: number };
  bossKilled: { x: number; y: number };
  floorEntered: { floor: number };
  /** The new floor's World is BUILT and current (fires inside loadFloor,
   *  after setWorld/skillOnFloorEnter; never for vault enter/exit; fires on
   *  floor 1 too). Arrival-effect artifacts hook HERE — `floorEntered` fires
   *  before the world swap and would shock the departing floor. */
  floorReady: { floor: number };
  /** Run clock crossed RUN_TIME_LIMIT_SECONDS — overtime pressure begins. */
  enrageStarted: Record<string, never>;
  ambushTriggered: { x: number; y: number };
  vaultEntered: { floor: number };
  vaultExited: { floor: number };
  keeperAwakened: { x: number; y: number };
  keeperKilled: { x: number; y: number };
  /** Mourne's Exhume: the swarm is whipped — faster, harder, specials first. */
  crawlersEnraged: { seconds: number };
  /** Wick's Flare: candle spent, the fury window opens. */
  flareIgnited: { x: number; y: number; seconds: number };
  /** Barrow's Bellring: all Ironbound stacks discharged in one slam. */
  bellringDischarged: { x: number; y: number; radius: number; stacks: number };
  /** Debris slab landed (ME4 hazard) — brown rubble ring at the impact. */
  debrisImpact: { x: number; y: number; radius: number };
  /** Treasure goblin: first noticed the player — escape clock armed. */
  goblinPanicked: { x: number; y: number };
  /** Escape clock expired — the goblin vanished, its loot lost for the run. */
  goblinEscaped: { x: number; y: number };
  goblinKilled: { x: number; y: number };
  altarChannelComplete: { x: number; y: number };
  /** `content` is the RESOLVED outcome (mend falls back to gems when the
   *  pocket is full); artifactId null on an artifact chest = pool exhausted. */
  chestOpened: { artifactId: string | null; content: 'artifact' | 'gems' | 'mend' };
  shrineTaken: { boonId: string; curseId: string };
  /** Wardstone (shop): a charge absorbed a landing hit outright. */
  wardBlocked: { chargesLeft: number };
  /** ME3 findables. */
  keyTaken: { x: number; y: number };
  brazierLit: { lit: number; total: number; window: number };
  relayReset: Record<string, never>;
  relayUnsealed: { x: number; y: number };
  springUsed: { healed: number };
  geodeShattered: { x: number; y: number };
  /** Leech heal pulse (enemy-side VFX — never rendered player-blue). */
  leechPulse: { x: number; y: number; radius: number; healed: boolean };
  /** Undertow drag pulse. */
  undertowPulse: { x: number; y: number; radius: number; hit: boolean };
  /** Rime chill pulse. */
  rimePulse: { x: number; y: number; radius: number; hit: boolean };
  /** Deep Freeze / Shatterpoint shatter. */
  shatter: { x: number; y: number };
  /** Rupture corpse detonation. */
  corpseDetonated: { x: number; y: number; radius: number };
  /** Caustic merge: a death inside a pool fed it (refresh/grow). */
  causticFed: { x: number; y: number; radius: number };
  /** Recoil retaliation nova. */
  novaFired: { x: number; y: number; radius: number };
  /** Knell: an elite kill tolled bonus bar fill. */
  knellToll: { x: number; y: number };
  /** Threshold/Settlement arrival shock. */
  thresholdShock: { x: number; y: number; radius: number };
  /** Leech Chain whip: polyline points, player origin first. */
  /** Chain visuals: 'leech' = Thirl's drink, 'arc' = Witchlight (cold/pale). */
  chainLash: { points: { x: number; y: number }[]; kind?: 'leech' | 'arc' };
  /** Aspect moments. */
  ninefoldSaved: Record<string, never>;
  candleEmpty: Record<string, never>;
  sunderStarted: Record<string, never>;
  sunderEnded: Record<string, never>;
  houndDashed: { x: number; y: number };
  houndDied: { x: number; y: number };
  /** Bram revived Gaunt at his body (M0 — ends the no-hound dead state). */
  houndRevived: { x: number; y: number };
  /** Generic AoE ring VFX (artifact hooks: retaliation, elite death toll). */
  burstFired: { x: number; y: number; radius: number };
  /** playerShots p2 splash burst on impact (0 = plain shot). */
  splashBurst: { x: number; y: number; radius: number };
  /** Pickup-track stat bump granted (no pause, no meter — toast only). */
  statBump: { index: number; label: string };
  /** A bump's bonus mend landed (PICKUP_HEAL_CHANCE roll + heal succeeded). */
  pickupHealed: { amount: number };
}

type Handler<T> = (payload: T) => void;

export class EventBus {
  private handlers = new Map<keyof GameEvents, Set<Handler<never>>>();

  on<K extends keyof GameEvents>(event: K, handler: Handler<GameEvents[K]>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as Handler<never>);
    return () => set.delete(handler as Handler<never>);
  }

  emit<K extends keyof GameEvents>(event: K, payload: GameEvents[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const h of set) (h as Handler<GameEvents[K]>)(payload);
  }

  clear(): void {
    this.handlers.clear();
  }
}
